import { useState, useRef, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

function generateId() {
  return Math.random().toString(36).substring(2, 15);
}

/**
 * Stream a workflow explanation via SSE.
 *
 * Stripped useChatStream: no chat persistence, no tools, no thinking.
 * Backend endpoint: POST /api/workflows/{workflowId}/explain/stream
 */
export default function useExplainStream({ workflowId } = {}) {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [explainedHash, setExplainedHash] = useState(null);
  const abortControllerRef = useRef(null);
  const messagesRef = useRef([]);

  const setMessagesAndRef = useCallback((updater) => {
    setMessages((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const updateAssistantMessage = useCallback(
    (updater) => {
      setMessagesAndRef((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = updater(last);
        }
        return next;
      });
    },
    [setMessagesAndRef]
  );

  const sendMessage = useCallback(
    async (text, { focusNodeId = null, suppressUserBubble = false } = {}) => {
      if (!workflowId || isStreaming) return;
      setError(null);

      // History sent to the backend (text only — assistant + user content).
      const apiMessages = [];
      for (const msg of messagesRef.current) {
        if (msg.role === 'user' && msg.content) {
          apiMessages.push({ role: 'user', content: msg.content });
        } else if (msg.role === 'assistant' && msg.content) {
          apiMessages.push({ role: 'assistant', content: msg.content });
        }
      }

      // For the auto-fired first explanation, suppress the user bubble in UI.
      const userMessage = suppressUserBubble
        ? null
        : { id: generateId(), role: 'user', content: text };

      // Only push the user turn into the API payload if we actually want it
      // surfaced; the backend constructs the synthetic first user turn when
      // we send no messages and no follow-up text.
      if (text && !suppressUserBubble) {
        apiMessages.push({ role: 'user', content: text });
      } else if (text && suppressUserBubble && apiMessages.length > 0) {
        // Edge case: should never happen in current callers.
        apiMessages.push({ role: 'user', content: text });
      }

      const assistantMessage = {
        id: generateId(),
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setMessagesAndRef((prev) => [
        ...prev,
        ...(userMessage ? [userMessage] : []),
        assistantMessage,
      ]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE_URL}/workflows/${workflowId}/explain/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({
              messages: apiMessages.length > 0 ? apiMessages : null,
              focus_node_id: focusNodeId,
            }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          updateAssistantMessage((msg) => ({
            ...msg,
            content: `Error: ${response.status} ${errorText}`,
            isStreaming: false,
          }));
          setError(`Request failed (${response.status})`);
          setIsStreaming(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';

          for (const part of parts) {
            if (!part.trim()) continue;
            const lines = part.split('\n');
            let eventType = '';
            let eventData = '';
            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.slice(7);
              else if (line.startsWith('data: ')) eventData = line.slice(6);
            }
            if (!eventType || !eventData) continue;

            let data;
            try {
              data = JSON.parse(eventData);
            } catch {
              continue;
            }

            switch (eventType) {
              case 'message_start':
                break;
              case 'content_delta':
                updateAssistantMessage((msg) => ({
                  ...msg,
                  content: msg.content + (data.text || ''),
                }));
                break;
              case 'message_done':
                updateAssistantMessage((msg) => ({
                  ...msg,
                  isStreaming: false,
                  usage: data.usage,
                }));
                break;
              case 'error':
                updateAssistantMessage((msg) => ({
                  ...msg,
                  content:
                    msg.content +
                    (msg.content ? '\n\n' : '') +
                    `Error: ${data.message}`,
                  isStreaming: false,
                }));
                setError(data.message);
                break;
              default:
                break;
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          updateAssistantMessage((msg) => ({ ...msg, isStreaming: false }));
        } else {
          updateAssistantMessage((msg) => ({
            ...msg,
            content:
              msg.content +
              (msg.content ? '\n\n' : '') +
              `Error: ${err.message}`,
            isStreaming: false,
          }));
          setError(err.message);
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [workflowId, isStreaming, setMessagesAndRef, updateAssistantMessage]
  );

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const clearMessages = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setMessagesAndRef([]);
    setError(null);
    setExplainedHash(null);
  }, [setMessagesAndRef]);

  // Abort on unmount.
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return {
    messages,
    isStreaming,
    error,
    explainedHash,
    setExplainedHash,
    sendMessage,
    stopStreaming,
    clearMessages,
  };
}
